<script setup lang="ts">
import { InfoTip } from '@it-tools/shell'
import { computed, reactive, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Workload } from '../model/fit'
import { formatCpu, formatMemory, parseCpu, parseMemory } from '../model/quantity'

const props = defineProps<{ modelValue: Workload }>()
const emit = defineEmits<{ 'update:modelValue': [Workload] }>()

const { t } = useI18n()

/**
 * Fields hold exactly what was typed, so `0.` or a half-written `512M` survive a
 * keystroke. Only a complete, valid workload is emitted; while any field is wrong
 * the results keep showing the last good one and the form says which field.
 */
type Field =
  'requestCpu' | 'requestMemory' | 'limitCpu' | 'limitMemory' | 'dsCpu' | 'dsMemory' | 'dsPods'

function draftsOf(workload: Workload): Record<Field, string> {
  return {
    requestCpu: formatCpu(workload.request.cpu),
    requestMemory: formatMemory(workload.request.memory),
    limitCpu: workload.limit.cpu === null ? '' : formatCpu(workload.limit.cpu),
    limitMemory: workload.limit.memory === null ? '' : formatMemory(workload.limit.memory),
    dsCpu: formatCpu(workload.daemonsets.cpu),
    dsMemory: formatMemory(workload.daemonsets.memory),
    dsPods: String(workload.daemonsets.pods),
  }
}

const drafts = reactive(draftsOf(props.modelValue))

// Only reset the drafts when the workload changes from outside (Reset, a restored
// link), never in response to our own emit, which would rewrite what is being typed.
let lastEmitted: Workload | null = null
watch(
  () => props.modelValue,
  (workload) => {
    if (workload !== lastEmitted) Object.assign(drafts, draftsOf(workload))
  },
)

const parsed = computed(() => {
  const optional = (
    text: string,
    parse: (s: string) => number | null,
  ): number | null | undefined => (text.trim() === '' ? null : (parse(text) ?? undefined))
  const pods = Number(drafts.dsPods)
  return {
    requestCpu: parseCpu(drafts.requestCpu),
    requestMemory: parseMemory(drafts.requestMemory),
    limitCpu: optional(drafts.limitCpu, parseCpu),
    limitMemory: optional(drafts.limitMemory, parseMemory),
    dsCpu: drafts.dsCpu.trim() === '' ? 0 : parseCpu(drafts.dsCpu),
    dsMemory: drafts.dsMemory.trim() === '' ? 0 : parseMemory(drafts.dsMemory),
    dsPods: drafts.dsPods.trim() === '' ? 0 : Number.isInteger(pods) && pods >= 0 ? pods : null,
  }
})

/** Kubernetes rejects a limit below its request, so this form does too. */
const errors = computed(() => {
  const p = parsed.value
  const below = (limit: number | null | undefined, request: number | null): boolean =>
    typeof limit === 'number' && request !== null && limit < request
  return {
    requestCpu: p.requestCpu === null,
    requestMemory: p.requestMemory === null,
    limitCpu: p.limitCpu === undefined || below(p.limitCpu, p.requestCpu),
    limitMemory: p.limitMemory === undefined || below(p.limitMemory, p.requestMemory),
    dsCpu: p.dsCpu === null,
    dsMemory: p.dsMemory === null,
    dsPods: p.dsPods === null,
  } satisfies Record<Field, boolean>
})

const invalid = computed(() => Object.values(errors.value).some(Boolean))
const limitBelow = computed(() => {
  const p = parsed.value
  return (
    (typeof p.limitCpu === 'number' && p.requestCpu !== null && p.limitCpu < p.requestCpu) ||
    (typeof p.limitMemory === 'number' &&
      p.requestMemory !== null &&
      p.limitMemory < p.requestMemory)
  )
})

function publish(): void {
  const p = parsed.value
  if (invalid.value) return
  const workload: Workload = {
    request: { cpu: p.requestCpu ?? 0, memory: p.requestMemory ?? 0 },
    limit: { cpu: p.limitCpu ?? null, memory: p.limitMemory ?? null },
    daemonsets: { cpu: p.dsCpu ?? 0, memory: p.dsMemory ?? 0, pods: p.dsPods ?? 0 },
  }
  lastEmitted = workload
  emit('update:modelValue', workload)
}

interface FieldSpec {
  readonly field: Field
  readonly label: string
  readonly placeholder: string
}

const pod = computed<readonly { legend: string; fields: readonly FieldSpec[] }[]>(() => [
  {
    legend: t('workload.requests'),
    fields: [
      { field: 'requestCpu', label: t('workload.cpu'), placeholder: '250m' },
      { field: 'requestMemory', label: t('workload.memory'), placeholder: '512Mi' },
    ],
  },
  {
    legend: t('workload.limits'),
    fields: [
      { field: 'limitCpu', label: t('workload.cpu'), placeholder: t('workload.noLimit') },
      { field: 'limitMemory', label: t('workload.memory'), placeholder: t('workload.noLimit') },
    ],
  },
])

const daemonsetFields = computed<readonly FieldSpec[]>(() => [
  { field: 'dsCpu', label: t('workload.cpu'), placeholder: '0' },
  { field: 'dsMemory', label: t('workload.memory'), placeholder: '0' },
  { field: 'dsPods', label: t('workload.pods'), placeholder: '0' },
])
</script>

<template>
  <form class="workload" @submit.prevent>
    <fieldset class="workload__group">
      <legend class="workload__legend">
        {{ t('workload.pod') }}
        <InfoTip :label="t('workload.pod')" :text="t('workload.podHelp')" />
      </legend>
      <div class="workload__row">
        <fieldset v-for="group in pod" :key="group.legend" class="workload__set">
          <legend class="workload__sublegend">{{ group.legend }}</legend>
          <div v-for="spec in group.fields" :key="spec.field" class="workload__field">
            <label :for="`workload-${spec.field}`">{{ spec.label }}</label>
            <input
              :id="`workload-${spec.field}`"
              v-model="drafts[spec.field]"
              class="mono"
              type="text"
              inputmode="decimal"
              autocomplete="off"
              spellcheck="false"
              :placeholder="spec.placeholder"
              :aria-invalid="errors[spec.field]"
              :aria-describedby="invalid ? 'workload-error' : undefined"
              @input="publish()"
            />
          </div>
        </fieldset>
      </div>
    </fieldset>

    <fieldset class="workload__group">
      <legend class="workload__legend">
        <span class="workload__swatch" aria-hidden="true" />
        {{ t('workload.daemonsets') }}
        <InfoTip :label="t('workload.daemonsets')" :text="t('workload.daemonsetsHelp')" />
      </legend>
      <!-- A sub-legend like the pod's Requests and Limits, so the inputs line up. -->
      <fieldset class="workload__set">
        <legend class="workload__sublegend">{{ t('workload.perNode') }}</legend>
        <div v-for="spec in daemonsetFields" :key="spec.field" class="workload__field">
          <label :for="`workload-${spec.field}`">{{ spec.label }}</label>
          <input
            :id="`workload-${spec.field}`"
            v-model="drafts[spec.field]"
            class="mono"
            type="text"
            :inputmode="spec.field === 'dsPods' ? 'numeric' : 'decimal'"
            autocomplete="off"
            spellcheck="false"
            :placeholder="spec.placeholder"
            :aria-invalid="errors[spec.field]"
            :aria-describedby="invalid ? 'workload-error' : undefined"
            @input="publish()"
          />
        </div>
      </fieldset>
    </fieldset>

    <p class="workload__hint">{{ t('workload.hint') }}</p>
    <p v-if="invalid" id="workload-error" class="workload__error" role="alert">
      {{ limitBelow ? t('workload.limitBelowRequest') : t('workload.invalid') }}
    </p>
  </form>
</template>

<style scoped>
.workload {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4) var(--space-6);
  align-items: flex-start;
}

.workload__group,
.workload__set {
  margin: 0;
  padding: 0;
  border: 0;
  min-width: 0;
}

.workload__legend {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-2);
  padding: 0;
  font-weight: 600;
}

.workload__sublegend {
  margin-bottom: var(--space-1);
  padding: 0;
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.workload__row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3) var(--space-4);
}

.workload__set {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: flex-end;
}

.workload__field {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

label {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

input {
  width: 7rem;
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface);
  color: var(--color-text);
  font-size: var(--text-base);
}

input[aria-invalid='true'] {
  border-color: var(--color-danger);
}

/* Ties the section to the DaemonSet colour in the node diagram. */
.workload__swatch {
  display: inline-block;
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 2px;
  background: var(--color-chart-2);
}

.workload__hint,
.workload__error {
  flex-basis: 100%;
  margin: 0;
  font-size: var(--text-sm);
}

.workload__hint {
  color: var(--color-text-muted);
}

.workload__error {
  color: var(--color-danger);
}
</style>
